import type {
  CallsRuntimeDecisionProjection,
} from './CallsRuntimeDecisionProjectionEngine';

import type {
  CallsRuntimeIncident,
} from './CallsRuntimeIncidentEngine';

import type {
  CallsIncidentResponsePlan,
} from './CallsIncidentResponsePlanningEngine';

import type {
  CallsRepairAuthorityResult,
} from './CallsRepairAuthorityGateEngine';

export type CallsPipelineStageName =
  | 'runtime-status'
  | 'live-evidence'
  | 'executive-decision'
  | 'incident'
  | 'response-plan'
  | 'repair-authority';

export type CallsPipelineStageState =
  | 'complete'
  | 'blocked'
  | 'withheld'
  | 'denied'
  | 'approved'
  | 'missing';

export type CallsPipelineStage = Readonly<{
  name: CallsPipelineStageName;
  state: CallsPipelineStageState;
  source: string | null;
  timestamp: string | null;
  reason: string | null;
  evidenceId: string | null;
}>;

export type CallsPipelineProjection = Readonly<{
  id: string;
  incidentKey: string;
  callSessionId: string | null;
  callId: string | null;
  stages: readonly CallsPipelineStage[];
  firstIncompleteStage: CallsPipelineStageName | null;
  nextRequiredGovernor: string | null;
  blocked: boolean;
  authorityApproved: boolean;
  repairExecuted: false;
  verificationComplete: false;
}>;

export type CallsPipelineProjectionInput = Readonly<{
  observations: readonly CallsRuntimeDecisionProjection[];
  incidents: readonly CallsRuntimeIncident[];
  responsePlans: readonly CallsIncidentResponsePlan[];
  authorityDecisions: readonly CallsRepairAuthorityResult[];
}>;

const freezeStage = (
  stage: CallsPipelineStage,
): CallsPipelineStage =>
  Object.freeze({ ...stage });

const missingStage = (
  name: CallsPipelineStageName,
): CallsPipelineStage =>
  freezeStage({
    name,
    state: 'missing',
    source: null,
    timestamp: null,
    reason: null,
    evidenceId: null,
  });

const findObservation = (
  incident: CallsRuntimeIncident,
  observations: readonly CallsRuntimeDecisionProjection[],
): CallsRuntimeDecisionProjection | null =>
  observations.find(
    observation =>
      (
        incident.callSessionId !== null &&
        observation.callSessionId ===
          incident.callSessionId
      ) ||
      (
        incident.callId !== null &&
        observation.callId ===
          incident.callId
      ),
  ) ?? null;

const authorityState = (
  decision: CallsRepairAuthorityResult,
): CallsPipelineStageState => {
  if (decision.decision === 'approved') {
    return 'approved';
  }

  if (decision.decision === 'denied') {
    return 'denied';
  }

  return 'withheld';
};

const nextGovernorForStage = (
  stage: CallsPipelineStageName | null,
): string | null => {
  switch (stage) {
    case 'runtime-status':
    case 'live-evidence':
    case 'executive-decision':
    case 'incident':
    case 'response-plan':
      return 'Calls Governor';

    case 'repair-authority':
      return 'Repair Governor';

    default:
      return 'Verification Governor';
  }
};

export function buildCallsEndToEndPipelines(
  input: CallsPipelineProjectionInput,
): readonly CallsPipelineProjection[] {
  const projections =
    input.incidents.map(incident => {
      const observation = findObservation(
        incident,
        input.observations,
      );

      const responsePlan =
        input.responsePlans.find(
          plan =>
            plan.incidentKey === incident.key,
        ) ?? null;

      const authorityDecision =
        input.authorityDecisions.find(
          decision =>
            decision.incidentKey ===
              incident.key,
        ) ?? null;

      const runtimeStatusStage =
        observation === null
          ? missingStage('runtime-status')
          : freezeStage({
              name: 'runtime-status',
              state:
                observation.status === 'unknown'
                  ? 'blocked'
                  : 'complete',
              source: observation.source,
              timestamp: observation.observedAt,
              reason: observation.status,
              evidenceId: null,
            });

      const liveEvidenceStage =
        observation === null
          ? missingStage('live-evidence')
          : freezeStage({
              name: 'live-evidence',
              state:
                observation.sharedEvidencePublished
                  ? 'complete'
                  : 'blocked',
              source: observation.source,
              timestamp: observation.observedAt,
              reason: observation.reasonCode,
              evidenceId:
                observation.sharedEvidencePublished
                  ? observation.callSessionId ??
                    observation.callId
                  : null,
            });

      const executiveDecisionStage =
        observation === null
          ? missingStage('executive-decision')
          : freezeStage({
              name: 'executive-decision',
              state:
                observation.executiveDecision ===
                'withhold-decision'
                  ? 'withheld'
                  : 'complete',
              source: 'Executive Governor',
              timestamp: observation.observedAt,
              reason:
                observation.executiveDecision,
              evidenceId: null,
            });

      const incidentStage =
        freezeStage({
          name: 'incident',
          state:
            incident.status === 'active'
              ? 'complete'
              : 'complete',
          source: incident.owner,
          timestamp: incident.lastObservedAt,
          reason: incident.reasonCode,
          evidenceId: incident.id,
        });

      const responsePlanStage =
        responsePlan === null
          ? missingStage('response-plan')
          : freezeStage({
              name: 'response-plan',
              state:
                responsePlan.status ===
                'awaiting-authority'
                  ? 'blocked'
                  : 'complete',
              source: responsePlan.owner,
              timestamp: responsePlan.createdAt,
              reason:
                responsePlan.recommendedAction,
              evidenceId: responsePlan.id,
            });

      const repairAuthorityStage =
        authorityDecision === null
          ? missingStage('repair-authority')
          : freezeStage({
              name: 'repair-authority',
              state:
                authorityState(
                  authorityDecision,
                ),
              source:
                authorityDecision
                  .requiredAuthority,
              timestamp:
                authorityDecision.evaluatedAt,
              reason:
                authorityDecision.reason,
              evidenceId:
                authorityDecision
                  .authorityEvidenceId,
            });

      const stages = Object.freeze([
        runtimeStatusStage,
        liveEvidenceStage,
        executiveDecisionStage,
        incidentStage,
        responsePlanStage,
        repairAuthorityStage,
      ]);

      const firstIncomplete =
        stages.find(
          stage =>
            stage.state !== 'complete' &&
            stage.state !== 'approved',
        ) ?? null;

      const authorityApproved =
        repairAuthorityStage.state ===
        'approved';

      return Object.freeze({
        id: `calls-pipeline:${incident.key}`,
        incidentKey: incident.key,
        callSessionId:
          incident.callSessionId,
        callId: incident.callId,
        stages,
        firstIncompleteStage:
          firstIncomplete?.name ?? null,
        nextRequiredGovernor:
          nextGovernorForStage(
            firstIncomplete?.name ?? null,
          ),
        blocked:
          firstIncomplete !== null,
        authorityApproved,
        repairExecuted: false,
        verificationComplete: false,
      });
    });

  return Object.freeze(projections);
}
